import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ManageListDialog } from 'components/common/pitch-lists/manage-list';
import { GameDataTab } from 'enums/game-data.enums';
import { t } from 'i18next';
import {
  IGameFilter,
  IPitchFilter,
  IPlayerFilter,
} from 'interfaces/i-mlb-browse';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import { BuildPriority, lookupPitchType } from 'lib_ts/enums/pitches.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMlbGame } from 'lib_ts/interfaces/mlb-stats-api/i-game';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { IMlbTeam } from 'lib_ts/interfaces/mlb-stats-api/i-team';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { compact, isEqual } from 'lodash';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MlbStatsService } from 'services/mlb-stats.service';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useListLibraryStore from 'stores/use-list-library-store';
import { AimingContext } from './aiming.context';
import { MachineContext } from './machine.context';

const CONTEXT_NAME = 'GameDataContext';

interface IGlobalFilter {
  sportId: MlbSportId;
  season?: number;
}

interface IBuildConfig {
  priority: BuildPriority;
  pitches: IMlbPitchExt[];
  isAverage?: boolean;
}

interface IImportPitchersDialog {
  key: number;
  pitchers: IMlbPlayer[];
}

interface IAddPitchesDialog {
  key: number;
  pitches: IPitch[];
  isAverage: boolean;
  // for pitcher name badge in add dialog
  player?: Nullable<IMlbPlayer>;
}

export interface IGameDataContext {
  loading: boolean;

  tab: GameDataTab;
  readonly setTab: (value: GameDataTab) => void;

  filterGlobal: IGlobalFilter;
  readonly mergeFilterGlobal: (value: Partial<IGlobalFilter>) => void;

  filterGames: Partial<IGameFilter>;
  readonly mergeFilterGames: (value: Partial<IGameFilter>) => void;

  filterPlayers: Partial<IPlayerFilter>;
  readonly mergeFilterPlayers: (value: Partial<IPlayerFilter>) => void;

  filterPitches: Partial<IPitchFilter>;
  readonly mergeFilterPitches: (value: Partial<IPitchFilter>) => void;

  seasons: IOption[];
  seasonGames: IMlbGame[];
  seasonPlayers: IMlbPlayer[];
  seasonTeams: IMlbTeam[];

  // callback is run if the toast is shown and the user presses accept
  readonly checkLaneWarning: (onAccept: () => void) => boolean;

  readonly buildPitches: (config: IBuildConfig) => Promise<IPitch[]>;

  dialogAddPitches?: IAddPitchesDialog;
  readonly closeAddDialog: () => void;
  readonly openAddDialog: (config: {
    guids?: IMlbPitchExt[];
    pitches?: IPitch[];
    pitcherPk?: number;
    isAverage?: boolean;
  }) => void;

  dialogImportPitchers?: IImportPitchersDialog;

  readonly closeImportPitchersDialog: () => void;
  readonly openImportPitchersDialog: (pitchers: IMlbPlayer[]) => void;
}

const DEFAULT: IGameDataContext = {
  loading: false,

  tab: 'pitchers',
  setTab: () => console.error(`${CONTEXT_NAME}: not init`),

  filterGlobal: {
    sportId: MlbSportId.MLB,
  },
  mergeFilterGlobal: () => console.error(`${CONTEXT_NAME}: not init`),

  filterGames: {},
  mergeFilterGames: () => console.error(`${CONTEXT_NAME}: not init`),

  filterPlayers: {},
  mergeFilterPlayers: () => console.error(`${CONTEXT_NAME}: not init`),

  filterPitches: {},
  mergeFilterPitches: () => console.error(`${CONTEXT_NAME}: not init`),

  seasons: [],

  seasonGames: [],

  seasonPlayers: [],

  seasonTeams: [],

  checkLaneWarning: () => false,

  buildPitches: () => new Promise(() => []),

  dialogAddPitches: undefined,
  openAddDialog: () => console.error(`${CONTEXT_NAME}: not init`),
  closeAddDialog: () => console.error(`${CONTEXT_NAME}: not init`),

  dialogImportPitchers: undefined,
  closeImportPitchersDialog: () => console.error(`${CONTEXT_NAME}: not init`),
  openImportPitchersDialog: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const GameDataContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

export const GameDataProvider: FC<IProps> = (props) => {
  const app = useCookiesStore((s) => s.app);
  const current = useAuthStore((s) => s.current);

  const { machine } = useContext(MachineContext);

  const { pitchBP } = useContext(AimingContext);

  // ? b/c the dialog needs to render pitcher options from this context
  const dialogManageList = useListLibraryStore((s) => s.dialogManage);

  const [warningAccepted, setWarningAccepted] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);

  const [loading, setLoading] = useState(DEFAULT.loading);

  const [filterGlobal, setFilterGlobal] = useState(DEFAULT.filterGlobal);
  const [filterGames, setFilterGames] = useState(DEFAULT.filterGames);
  const [filterPlayers, setFilterPlayers] = useState(DEFAULT.filterPlayers);
  const [filterPitches, setFilterPitches] = useState(DEFAULT.filterPitches);

  const [seasons, setSeasons] = useState(DEFAULT.seasons);

  const [seasonGames, setSeasonGames] = useState(DEFAULT.seasonGames);
  const [seasonTeams, setSeasonTeams] = useState(DEFAULT.seasonTeams);
  const [seasonPlayers, setSeasonPlayers] = useState(DEFAULT.seasonPlayers);

  // init by loading seasons & finding the most recent season with games
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        if (!current.mlb_stats_api) {
          return;
        }

        if (filterGlobal.season) {
          // we already found a season to set as a filter, no need to fetch anything
          return;
        }

        setLoading(true);

        // load seasons
        const seasons = await MlbStatsService.getInstance().countGames({
          sportId: filterGlobal.sportId,
        });

        seasons.sort((a, b) => (a.season > b.season ? -1 : 1));

        const nextSeasons = seasons.map((m) => {
          const o: IOption = {
            label: m.season.toString(),
            value: m.season.toString(),
            disabled: m.total === 0,
          };

          return o;
        });

        setSeasons(nextSeasons);

        // find the first season with games played
        const mostRecentSeasonWithGames = nextSeasons.find((s) => !s.disabled);

        if (!mostRecentSeasonWithGames) {
          // this shouldn't ever happen
          return;
        }

        const defaultSeason = parseInt(mostRecentSeasonWithGames.value);

        setFilterGlobal((prev) => ({
          ...prev,
          season: defaultSeason,
        }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadSeasons();
  }, [current.mlb_stats_api, filterGlobal.sportId, filterGlobal.season]);

  useEffect(() => {
    // once we've found the most recent season above, load the teams, games, and players
    const loadGamesAndPlayers = async () => {
      try {
        if (!current.mlb_stats_api) {
          // user is not allowed to use Stats API
          return;
        }

        if (!filterGlobal.season) {
          // can't do anything yet, wait for the season filter to be set
          return;
        }

        setLoading(true);

        const teams = await MlbStatsService.getInstance().getTeams({
          season: filterGlobal.season,
          sportId: filterGlobal.sportId,
        });
        setSeasonTeams(teams);

        // ensure that the team for each player is always based on the current rosters
        const currentTeams =
          await MlbStatsService.getInstance().getCurrentTeams({
            sportId: filterGlobal.sportId,
          });

        const players = await MlbStatsService.getInstance().getPlayers({
          season: filterGlobal.season,
          sportId: filterGlobal.sportId,
          teams: currentTeams,
        });
        setSeasonPlayers(players);

        const games = await MlbStatsService.getInstance().getGames({
          season: filterGlobal.season,
          sportId: filterGlobal.sportId,
        });

        games.sort(
          (a, b) => -StringHelper.safeSort(a.officialDate, b.officialDate)
        );
        setSeasonGames(games);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const timeout = setTimeout(loadGamesAndPlayers, 100);
    return () => clearTimeout(timeout);
  }, [
    current.mlb_stats_api,
    filterGlobal.sportId,
    filterGlobal.season,
    seasons,
  ]);

  useEffect(() => {
    // reset the player and game filters when the sport or season changes
    setFilterPlayers((prev) => ({
      ...prev,
      teamPk: undefined,
    }));

    setFilterGames((prev) => ({
      ...prev,
      teamPk: undefined,
    }));
  }, [filterGlobal.sportId, filterGlobal.season]);

  const [tab, setTab] = useState(DEFAULT.tab);

  const [dialogAddPitches, setDialogAddPitches] = useState<IAddPitchesDialog>();

  const [dialogImportPitchers, setDialogImportPitchers] =
    useState<IImportPitchersDialog>();

  const buildPitches = useCallback(
    async (config: IBuildConfig): Promise<IPitch[]> => {
      const validGuids: IMlbPitchExt[] = [];
      const errors: string[] = [];

      config.pitches.forEach((g) => {
        const errs = MlbStatsHelper.getErrors(g);
        if (errs.length === 0) {
          validGuids.push(g);
        } else {
          errors.push(...errs);
        }
      });

      if (errors.length > 0) {
        NotifyHelper.warning({
          message_md: [
            'Invalid data detected:',
            ArrayHelper.unique(errors)
              .map((e) => ` - ${e}`)
              .join('\n'),
            `${
              config.pitches.length === 1 ? 'This pitch' : 'Affected pitches'
            } cannot be constructed at this time.`,
          ].join('\n\n'),
        });
      }

      if (validGuids.length === 0) {
        return [];
      }

      const convertedResults = validGuids.map((g) =>
        // average pitches will point to the default plate
        MlbStatsHelper.convertToChars(g, config.priority, !!config.isAverage)
      );

      if (app.game_data_import_options.average_release) {
        MlbStatsHelper.averageReleasesByPitcher(validGuids, convertedResults);
      }

      const output = compact(
        convertedResults.map((converted) => {
          if (!converted.bs) {
            return undefined;
          }

          if (!converted.traj) {
            return undefined;
          }

          const play = validGuids.find((m) => converted.guid === m.guid);

          if (!play) {
            // shouldn't trigger
            return undefined;
          }

          const gamePk = play.gamePk;
          const game = seasonGames.find((g) => g.gamePk === gamePk);

          const pitchType = lookupPitchType(play.type);

          const pitch: IPitch = {
            mlb_gamePk: config.isAverage ? undefined : play.gamePk,
            mlb_guid: config.isAverage ? undefined : play.guid,

            hitter: config.isAverage ? undefined : play.batter,
            game: config.isAverage
              ? undefined
              : game
                ? `${game.officialDate}: ${game.away.name} @ ${game.home.name}`
                : undefined,
            outcome: config.isAverage ? undefined : play.outcome,

            bs: converted.bs,
            bs_hash: MachineHelper.getBsHash(converted.bs, config.priority),
            traj: converted.traj,

            seams: converted.seams,
            breaks: TrajHelper.getBreaks(converted.traj),
            priority: config.priority,

            plate_loc_backup: TrajHelper.getPlateLoc(converted.traj),

            name: [
              config.isAverage ? 'Avg.' : undefined,
              play.pitcher,
              pitchType,
              // char.bs.vnet.toFixed(0),
            ]
              .filter((s) => !!s)
              .join(' '),

            type: pitchType,

            windupVideoID: null,
            stretchVideoID: null,

            // year: this.props.browseCx.gameFilter.season,
            _id: play.guid,
            _created: new Date().toISOString(),
            _changed: new Date().toISOString(),
            _parent_def: '',
            _parent_id: '',
          };

          return pitch;
        })
      );

      return output;
    },
    [app, machine, seasonGames]
  );

  const state: IGameDataContext = {
    loading: loading,

    tab: tab,
    setTab: setTab,

    filterGlobal: filterGlobal,
    mergeFilterGlobal: async (value) => {
      setFilterGlobal((prev) => {
        const nextValue: IGlobalFilter = {
          ...prev,
          ...value,
        };

        if (isEqual(nextValue, filterGlobal)) {
          // do nothing
          return prev;
        }

        if (nextValue.sportId !== filterGlobal.sportId) {
          // unset it so that the logic picks the first non-empty season when the games load
          nextValue.season = undefined;
        }

        return nextValue;
      });
    },

    filterGames: filterGames,
    mergeFilterGames: (value) =>
      setFilterGames((prev) => ({
        ...prev,
        ...value,
      })),

    filterPlayers: filterPlayers,
    mergeFilterPlayers: (value) =>
      setFilterPlayers((prev) => ({
        ...prev,
        ...value,
      })),

    filterPitches: filterPitches,
    mergeFilterPitches: (value) =>
      setFilterPitches((prev) => ({
        ...prev,
        ...value,
      })),

    seasons: seasons,
    seasonGames: seasonGames,
    seasonPlayers: seasonPlayers,
    seasonTeams: seasonTeams,

    checkLaneWarning: (onAccept) => {
      if (warningAccepted) {
        // allow user to S2M
        return true;
      }

      if (warningVisible) {
        // toast is already visible
        return false;
      }

      // prevents subsequent triggers while toast is open from spawning additional toasts
      setWarningVisible(true);

      NotifyHelper.warning({
        message_md: `Before test firing, please ensure the lane is clear for safety reasons.`,
        delay_ms: 0,
        // if the user dismisses without accepting, the next trigger will resurface the toast
        onClose: () => setWarningVisible(false),
        buttons: [
          {
            label: t('common.accept'),
            dismissAfterClick: true,
            onClick: () => {
              // user will not be prompted again for the rest of the session
              setWarningAccepted(true);

              // e.g. re-trigger S2M
              onAccept();
            },
          },
        ],
      });

      return false;
    },

    buildPitches: buildPitches,

    openAddDialog: async (config) => {
      const built: IPitch[] = [];

      if (config.pitches) {
        built.push(...config.pitches);
      }

      if (config.guids) {
        const builtGuids = await buildPitches({
          priority: app.game_data_import_options.priority,
          pitches: config.guids,
          isAverage: config.isAverage,
        });

        built.push(...builtGuids);
      }

      if (built.length === 0) {
        return;
      }

      // fix the videos if necessary
      if (pitchBP?.stretchVideoID) {
        const selected = built.find((p) => p.mlb_guid === pitchBP?.mlb_guid);

        if (selected) {
          selected.stretchVideoID = pitchBP.stretchVideoID;
        }
      }

      if (pitchBP?.windupVideoID) {
        const selected = built.find((p) => p.mlb_guid === pitchBP?.mlb_guid);

        if (selected) {
          selected.windupVideoID = pitchBP.windupVideoID;
        }
      }

      const player = seasonPlayers.find((p) => p.playerPk === config.pitcherPk);

      setDialogAddPitches({
        key: Date.now(),
        pitches: built,
        isAverage: !!config.isAverage,
        player,
      });
    },

    dialogImportPitchers: dialogImportPitchers,

    closeImportPitchersDialog: () => setDialogImportPitchers(undefined),
    openImportPitchersDialog: (pitchers) =>
      setDialogImportPitchers({
        key: Date.now(),
        pitchers: pitchers,
      }),

    dialogAddPitches: dialogAddPitches,

    closeAddDialog: () => setDialogAddPitches(undefined),
  };

  return (
    <GameDataContext.Provider value={state}>
      {props.children}

      {dialogManageList && (
        <ManageListDialog
          key={dialogManageList.key}
          identifier={dialogManageList.dialog.identifier}
          mode={dialogManageList.dialog.mode}
          onCreated={dialogManageList.dialog.onCreated}
          onClose={dialogManageList.dialog.onClose}
        />
      )}
    </GameDataContext.Provider>
  );
};
